import { Badge } from '@/components/ui/badge'
import type { RunStatus } from '@/lib/types'
import { cn } from '@/lib/utils'

export interface RunStatusBadgeProps {
  /** Run status */
  status: RunStatus
  /** Badge size */
  size?: 'sm' | 'md'
  /** Whether to show the label */
  showLabel?: boolean
  /** Additional CSS classes */
  className?: string
}

const statusConfig: Record<
  RunStatus,
  { label: string; shortLabel: string; variant: string; animate?: boolean }
> = {
  PENDING: {
    label: 'Pending',
    shortLabel: 'PEND',
    variant: 'pending',
  },
  QUEUED: {
    label: 'Queued',
    shortLabel: 'QUEUE',
    variant: 'queued',
  },
  RUNNING: {
    label: 'Running',
    shortLabel: 'RUN',
    variant: 'running',
    animate: true,
  },
  SUCCEEDED: {
    label: 'Succeeded',
    shortLabel: 'OK',
    variant: 'succeeded',
  },
  FAILED: {
    label: 'Failed',
    shortLabel: 'FAIL',
    variant: 'failed',
  },
  CANCELLED: {
    label: 'Cancelled',
    shortLabel: 'CANC',
    variant: 'cancelled',
  },
  TIMEOUT: {
    label: 'Timeout',
    shortLabel: 'TMOUT',
    variant: 'timeout',
  },
}

/**
 * Color-coded status badge with optional animation for running state.
 * 
 * @example
 * ```tsx
 * <RunStatusBadge status="RUNNING" />
 * <RunStatusBadge status="SUCCEEDED" size="sm" showLabel={false} />
 * ```
 */
export function RunStatusBadge({
  status,
  size = 'md',
  showLabel = true,
  className,
}: RunStatusBadgeProps) {
  const config = statusConfig[status] || statusConfig.PENDING

  return (
    <Badge
      variant={config.variant as any}
      className={cn(
        config.animate && 'animate-pulse',
        size === 'sm' && 'text-xs px-1.5 py-0',
        className
      )}
    >
      {showLabel ? config.shortLabel : config.label}
    </Badge>
  )
}

/**
 * Get the full label for a status
 */
export function getStatusLabel(status: RunStatus): string {
  return statusConfig[status]?.label || status
}
